import React from "react";

import { KeyBadge } from "../../atoms/KeyBadge";
import { KgNodeFrame } from "../../atoms/KgNodeFrame";
import { KgCollapsedNodeHover } from "../KgCollapsedNodeHover";
import { KgTableIcon } from "../../atoms/KgTableIcon";
import { rawColors } from "../../../design-system/tokens/colors";
import {
  FieldList,
  FieldName,
  FieldRow,
  Header,
  MoreFooter,
  MoreText,
  TableCardRoot,
  TableName,
} from "./KgTableNodeCard.styles";
import type { KgTableNodeCardProps } from "./KgTableNodeCard.types";

const DEFAULT_MAX_VISIBLE = 3;

export const KgTableNodeCard: React.FC<KgTableNodeCardProps> = ({
  tableName,
  fields,
  maxVisible = DEFAULT_MAX_VISIBLE,
  expanded = false,
  collapsed = false,
  visualState = "default",
  isSelected = false,
  onClick,
}) => {
  if (collapsed) {
    return (
      <KgCollapsedNodeHover
        icon={<KgTableIcon />}
        color={rawColors.accent.tableBlue}
        bgColor={rawColors.accent.tableBlueBg}
        visualState={visualState}
        title={tableName}
        subtitle="Table"
      />
    );
  }

  const visibleFields = expanded ? fields : fields.slice(0, maxVisible);
  // Clipped height = maxVisible rows; expanded height = all rows with generous buffer.
  const hiddenCount = Math.max(0, fields.length - maxVisible);
  return (
    <TableCardRoot>
      <KgNodeFrame
        category="table"
        visualState={visualState}
        isSelected={isSelected}
        flush
        onClick={onClick}
      >
        <Header>
          <KgTableIcon />
          <TableName title={tableName}>{tableName}</TableName>
        </Header>

        {/*
          Expanded lists cap at a fixed height and scroll internally.
          "nowheel" tells React Flow to let the wheel scroll the list
          instead of zooming the canvas.
        */}
        <FieldList
          $expanded={expanded}
          className={expanded ? "nowheel" : undefined}
        >
          {visibleFields.map((field) => (
            <FieldRow key={field.name}>
              {field.keyType && <KeyBadge kind={field.keyType} />}
              <FieldName title={field.name}>{field.name}</FieldName>
            </FieldRow>
          ))}
        </FieldList>

        {hiddenCount > 0 && (
          <MoreFooter $visible={!expanded}>
            <MoreText>{expanded ? null : `+ ${hiddenCount} more`}</MoreText>
          </MoreFooter>
        )}
      </KgNodeFrame>
    </TableCardRoot>
  );
};

export default KgTableNodeCard;
